import { memo } from "react";
import { useTheme } from "../../theme/ThemeContext";

const SOURCES = {
  icon: { dark: "/brand/logo-icon-dark.png", light: "/brand/logo-icon-light.png" },
  lockup: {
    dark: { png: "/brand/logo-lockup-dark.png", webp: "/brand/logo-lockup-dark.webp" },
    light: { png: "/brand/logo-lockup-light.png", webp: "/brand/logo-lockup-light.webp" },
  },
};

/**
 * Gulf TechLab gear-chip brand mark. Picks the dark/light-suited asset
 * based on the active theme (the "dark" files use paler colors tuned for
 * a dark canvas; "light" files use deeper teal tuned for a light canvas).
 *
 * Props:
 *  - size: number              pixel size (default 48)
 *  - variant: "icon" | "lockup"  icon = mark only (pair with your own
 *      wordmark text); lockup = mark + "GULF TECH LAB" baked in (default "icon")
 *  - withGlow: bool            cyan halo behind the mark (default true)
 *  - className: string
 */
const AnimatedLogo = memo(({ size = 48, variant = "icon", withGlow = true, className = "" }) => {
  const { theme } = useTheme();
  const isLockup = variant === "lockup";
  const aspect = isLockup ? 1600 / 1062 : 1;

  return (
    <div
      className={`relative inline-block ${className}`}
      style={{ width: size, height: isLockup ? size / aspect : size }}
      aria-label="Gulf TechLab"
      data-testid="animated-logo"
    >
      {withGlow && (
        <div
          className="absolute inset-0 rounded-full blur-2xl pointer-events-none animate-pulse-glow"
          style={{
            background: "radial-gradient(circle, rgba(0,229,255,0.45) 0%, rgba(0,229,255,0) 70%)",
          }}
        />
      )}
      {isLockup ? (
        <picture>
          <source type="image/webp" srcSet={SOURCES.lockup[theme].webp} />
          <img
            src={SOURCES.lockup[theme].png}
            alt="Gulf TechLab"
            loading="eager"
            decoding="sync"
            fetchPriority="high"
            className="relative z-10 w-full h-full object-contain select-none"
            draggable={false}
          />
        </picture>
      ) : (
        <img
          src={SOURCES.icon[theme]}
          alt="Gulf TechLab"
          loading="eager"
          decoding="sync"
          fetchPriority="high"
          className="relative z-10 w-full h-full object-contain select-none"
          draggable={false}
        />
      )}
    </div>
  );
});

AnimatedLogo.displayName = "AnimatedLogo";

export default AnimatedLogo;
